import React from "react";
import {StyledEngineProvider, ThemeProvider} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {unstable_useEnhancedEffect as useEnhancedEffect} from "@mui/material/utils";
import {ThemeProvider as ScThemeProvider} from "styled-components";
import {ThemeModeProvider, useThemeMode} from "./ThemeModeProvider";
import {darkTheme, lightTheme} from "./theme";
import {THEME_VARS_STYLE} from "./cssVars";

/** The single id under which the token -> CSS-variable bridge <style> lives in <head>. */
const THEME_VARS_STYLE_ID = "tf-theme-vars";

/**
 * Mounts the token -> CSS-variable bridge (ticket #142): a single <style> carrying BOTH modes (light
 * on :root, dark under :root[data-theme="dark"]) appended once to <head>. Injected via a layout
 * effect (one of the two hydration-safe mechanisms the ticket sanctions) rather than rendered in the
 * tree, so the React output stays byte-identical to the SSG pre-render and never pollutes a
 * component's DOM subtree. The active mode is selected by the `data-theme` attribute that
 * ThemeModeProvider stamps on the document element, so CSS Modules can read `var(--tf-*)` and follow
 * light/dark for free. Deduped by id, so the many AppTheme mounts in tests share one style node.
 */
function ThemeVarsBridge(): null {
	useEnhancedEffect(() => {
		if (typeof document === "undefined" || document.getElementById(THEME_VARS_STYLE_ID)) {
			return;
		}
		const style = document.createElement("style");
		style.id = THEME_VARS_STYLE_ID;
		style.textContent = THEME_VARS_STYLE;
		document.head.appendChild(style);
	}, []);
	return null;
}

/**
 * The complete v3 theme stack: mode state + the MUI and styled-components providers, both fed the
 * same mode-matched theme object so `styled.div` and MUI components always read one palette.
 *
 * - `StyledEngineProvider injectFirst` keeps MUI's style sheet ahead of styled-components.
 * - `CssBaseline` lives here so the document background/text flip with the mode.
 *
 * `AppProviders` wraps every route with this; tests can use it standalone as a lightweight harness.
 */
export function AppTheme({children}: { children: React.ReactNode }) {
	return (
		<ThemeModeProvider>
			<ModeThemeBridge>{children}</ModeThemeBridge>
		</ThemeModeProvider>
	);
}

/** Selects the mode-matched theme; separate component so it can read the mode context. */
function ModeThemeBridge({children}: { children: React.ReactNode }) {
	const {mode} = useThemeMode();
	const activeTheme = mode === "dark" ? darkTheme : lightTheme;
	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={activeTheme}>
				<ScThemeProvider theme={activeTheme}>
					<CssBaseline/>
					<ThemeVarsBridge/>
					{children}
				</ScThemeProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	);
}
